import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useApi } from '../contexts/ApiContext';

const Upload = () => {
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);
  const { uploadFile, getUploadHistory: getUploadHistoryApi } = useApi();

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Check for supported file types
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.csv') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
      setUploadStatus({
        type: 'error',
        message: 'Please upload a CSV or Excel (.xlsx, .xls) file'
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit due to API Gateway
      setUploadStatus({
        type: 'error',
        message: 'File size must be less than 10MB due to API limitations. For larger files, please split them into smaller chunks.'
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus(null);

    try {
      setUploadProgress(30);

      // Upload to API
      const response = await uploadFile(file);

      setUploadProgress(100);

      setUploadStatus({
        type: 'success',
        message: `File "${file.name}" uploaded successfully! ${response.detectedType ? `Detected as ${response.detectedType} file. ` : ''}Processing ${response.recordCount || 'unknown'} records.`
      });

      // Refresh upload history
      await loadUploadHistory();

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({
        type: 'error',
        message: error.response?.data?.error || error.message || 'Upload failed'
      });
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  }, [uploadFile]);

  const loadUploadHistory = async () => {
    try {
      const response = await getUploadHistoryApi();
      setUploadHistory(response.uploads || []);
    } catch (error) {
      console.error('Error loading upload history:', error);
    }
  };

  React.useEffect(() => {
    loadUploadHistory();
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/excel': ['.xls'],
      'application/x-excel': ['.xls'],
      'application/x-msexcel': ['.xls']
    },
    multiple: false
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'processing':
        return <ScheduleIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <ScheduleIcon color="info" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Upload Data
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload CSV or Excel files containing settlement deposit data, daily transactions, or PAI transactions. Files must include the required columns and be under 10MB.
      </Typography>

      {/* Upload Area */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover'
              }
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop your file here' : 'Drag & drop a CSV or Excel file here, or click to select'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Maximum file size: 10MB (for larger files, please split into chunks)
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              disabled={uploading}
            >
              Select File
            </Button>
          </Box>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}

          {uploadStatus && (
            <Alert 
              severity={uploadStatus.type} 
              sx={{ mt: 2 }}
              onClose={() => setUploadStatus(null)}
            >
              {uploadStatus.message}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Required CSV Format */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Required CSV Format
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your CSV file must include the following columns in any order:
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>File Size Limitation:</strong> Due to API Gateway limits, files must be under 10MB. 
            For larger datasets, please split your CSV into multiple smaller files and upload them separately.
          </Alert>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="TerminalID" 
                secondary="Unique identifier for the terminal" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="LocationName" 
                secondary="Name or description of the location" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="AccountHolder" 
                secondary="Name of the account holder" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="RTA" 
                secondary="RTA identifier" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="SettlementDate" 
                secondary="Date of settlement (YYYY-MM-DD format)" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="ProcessedDate" 
                secondary="Date processed (YYYY-MM-DD format)" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="DepositType" 
                secondary="Type of deposit (Credit, Debit, etc.)" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="TransactionCount" 
                secondary="Number of transactions (integer)" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Amount" 
                secondary="Settlement amount (decimal number)" 
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Upload History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload History
          </Typography>
          {uploadHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No uploads yet
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Upload Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Records</TableCell>
                    <TableCell>Size</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploadHistory.map((upload) => (
                    <TableRow key={upload.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {upload.file_name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {new Date(upload.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(upload.status)}
                          label={upload.status}
                          color={getStatusColor(upload.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{upload.records_processed || 0}</TableCell>
                      <TableCell>
                        {(upload.file_size / 1024).toFixed(1)} KB
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Upload;